/**
 * Permission Resources Configuration
 * 40 granular permission resources organized by category (kept in sync with
 * backend granularPermissionCheck.js VALID_RESOURCES — must match exactly;
 * adminResourceEnforcement.js throws at load if a route rule names a resource
 * that VALID_RESOURCES does not contain, so the three files move together)
 *
 * This replaces the legacy 3-tier role system with granular permissions
 * Each user can have any combination of resources with full/view_only access
 */

// ============================================================================
// SUPER ADMIN EMAILS (hardcoded for safety)
// ============================================================================

/**
 * List of emails that should ALWAYS have super admin access
 * These users can never be locked out and have full access to everything
 */
export const SUPER_ADMIN_EMAILS: string[] = [
  'grant@trylifescribe.com',
  'developer@trylifescribe.com',
];

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type AccessLevel = 'full' | 'view_only' | 'none';

export interface Permission {
  resource: string;
  access_level: AccessLevel;
}

export interface PermissionCategory {
  id: string;
  label: string;
  color: string;
  description: string;
}

export interface PermissionResource {
  id: string;
  label: string;
  icon: string;
  category: string;
  color: string;
  description: string;
}

// 7 Permission Categories
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'financial',
    label: 'Financial',
    color: '#10B981',
    description: 'Revenue, costs, and financial analytics'
  },
  {
    id: 'users',
    label: 'Users & Admin',
    color: '#6366F1',
    description: 'User management and administration'
  },
  {
    id: 'content',
    label: 'Content',
    color: '#F59E0B',
    description: 'Chapters, quests, and content creation'
  },
  {
    id: 'ai',
    label: 'AI & Styles',
    color: '#EC4899',
    description: 'AI transformation and styling'
  },
  {
    id: 'communications',
    label: 'Communications',
    color: '#8B5CF6',
    description: 'Email, notifications, and messaging'
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    color: '#EF4444',
    description: 'AWS, website status, and analytics'
  },
  {
    id: 'system',
    label: 'System',
    color: '#64748B',
    description: 'Audit logs and system settings'
  },
];

// 40 Permission Resources (must match backend VALID_RESOURCES)
export const PERMISSION_RESOURCES: PermissionResource[] = [
  // Financial (4)
  {
    id: 'overview',
    label: 'Overview',
    icon: '📊',
    category: 'financial',
    color: '#10B981',
    description: 'Main dashboard overview with key metrics'
  },
  {
    id: 'revenue_analytics',
    label: 'Revenue Analytics',
    icon: '💰',
    category: 'financial',
    color: '#10B981',
    description: 'Revenue reports and financial analytics'
  },
  {
    id: 'cost_management',
    label: 'Cost Management',
    icon: '📈',
    category: 'financial',
    color: '#10B981',
    description: 'AWS and operational cost tracking'
  },
  {
    id: 'stripe_discounts',
    label: 'Stripe Discounts',
    icon: '🎫',
    category: 'financial',
    color: '#10B981',
    description: 'Manage coupons and discount codes'
  },

  // Users & Admin (3)
  {
    id: 'user_management',
    label: 'User Management',
    icon: '👥',
    category: 'users',
    color: '#6366F1',
    description: 'View and manage app users'
  },
  {
    id: 'admin_users',
    label: 'Admin Users',
    icon: '🛡️',
    category: 'users',
    color: '#6366F1',
    description: 'Manage admin user accounts and permissions'
  },
  {
    id: 'subscription_analytics',
    label: 'Subscription Analytics',
    icon: '📋',
    category: 'users',
    color: '#6366F1',
    description: 'Subscription metrics and churn analysis'
  },
  {
    id: 'free_tier_users',
    label: 'Free Tier Users',
    icon: '🎯',
    category: 'users',
    color: '#6366F1',
    description: 'Free-tier conversion intelligence, nurture funnels, and cart recovery'
  },

  // Content (5)
  {
    id: 'chapter_creation',
    label: 'Chapter Creation',
    icon: '📖',
    category: 'content',
    color: '#F59E0B',
    description: 'Create and manage chapter templates'
  },
  {
    id: 'quest_creation',
    label: 'Quest Creation',
    icon: '🗺️',
    category: 'content',
    color: '#F59E0B',
    description: 'Create and manage quest content'
  },
  {
    id: 'legacy_quests',
    label: 'Legacy Quests',
    icon: '🏆',
    category: 'content',
    color: '#F59E0B',
    description: 'Manage legacy quest system'
  },
  {
    id: 'daily_inspirations',
    label: 'Daily Inspirations',
    icon: '✨',
    category: 'content',
    color: '#F59E0B',
    description: 'Create and schedule daily inspirations'
  },
  {
    id: 'pdf_templates',
    label: 'PDF Templates',
    icon: '📄',
    category: 'content',
    color: '#F59E0B',
    description: 'Manage PDF export templates'
  },

  // AI & Styles (4)
  {
    id: 'ai_styles',
    label: 'AI Styles',
    icon: '🎨',
    category: 'ai',
    color: '#EC4899',
    description: 'Manage AI writing styles'
  },
  {
    id: 'ai_moods',
    label: 'AI Moods',
    icon: '😊',
    category: 'ai',
    color: '#EC4899',
    description: 'Configure AI mood transformations'
  },
  {
    id: 'ai_monitor',
    label: 'AI Monitor',
    icon: '🤖',
    category: 'ai',
    color: '#EC4899',
    description: 'Monitor AI service performance'
  },
  {
    id: 'brain_health',
    label: 'Brain Health',
    icon: '🧠',
    category: 'ai',
    color: '#EC4899',
    description: 'Manage brain health features'
  },
  {
    id: 'grok_imagine',
    label: 'Grok Imagine Video',
    icon: '🎬',
    category: 'ai',
    color: '#EC4899',
    description: 'Manage AI video generation prompt variations and analytics'
  },
  {
    id: 'quill_admin',
    label: 'Quill AI Administration',
    icon: '🪶',
    category: 'ai',
    color: '#EC4899',
    description: 'Controls the Quill AI platform (models, weights, reasoning prompts, guardrails, interview bank, coverage, and fidelity dashboards) — writes to the Quill production DB via a locally-run backend'
  },
  {
    id: 'lifeadvice_admin',
    label: 'LifeAdvice Administration',
    icon: '💡',
    category: 'ai',
    color: '#D4AC0D',
    description: 'Manage LifeAdvice: wisdom pillars, prompts, and the curated library, plus read-only member entries and the creation/share analytics. Content changes go live in the app immediately; member entries can be viewed and exported but never edited'
  },

  // Wedding Books (1)
  {
    id: 'wedding_books',
    label: 'Wedding Books',
    icon: '💍',
    category: 'content',
    color: '#F59E0B',
    description: 'Wedding Life Book portfolio — books, couples, guests, purchases and cross-sell conversions; suspend/reactivate books, extend deadlines, resend guest invitations'
  },

  // Communications (11)
  {
    id: 'comms_overview',
    label: 'Communications Overview',
    icon: '📡',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Marketing & nurture overview, funnels, and triggers'
  },
  {
    id: 'email_campaigns',
    label: 'Email Campaigns',
    icon: '📨',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Create and send marketing email campaigns'
  },
  {
    id: 'email_hub',
    label: 'Email Hub',
    icon: '🗂️',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Unified email management hub'
  },
  {
    id: 'push_notifications',
    label: 'Push Notifications',
    icon: '🔔',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Send and manage push notification campaigns'
  },
  {
    id: 'sms_campaigns',
    label: 'SMS Campaigns',
    icon: '📱',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Create and send SMS campaigns'
  },
  {
    id: 'audience_manager',
    label: 'Audience Manager',
    icon: '🎯',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Manage marketing audiences and segments'
  },
  {
    id: 'customer_support',
    label: 'Customer Support',
    icon: '🎧',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Manage voicemails, emails, and callbacks'
  },
  {
    id: 'in_app_text_email',
    label: 'In-App Text/Email',
    icon: '💬',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Send in-app notifications and emails'
  },
  {
    id: 'email_templates',
    label: 'Email Templates',
    icon: '📧',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Manage email template designs'
  },
  {
    id: 'email_triggers',
    label: 'Email Triggers',
    icon: '⚡',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Configure automated email triggers'
  },
  {
    id: 'transactional_triggers',
    label: 'Email Triggers',
    icon: '⚡',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Edit transactional email triggers. HC-3 override control (2026-08-18): edits are permitted only with this permission and every change is audit-logged.'
  },
  {
    id: 'family_collaboration',
    label: 'Family Collaboration',
    icon: '👨‍👩‍👧',
    category: 'communications',
    color: '#8B5CF6',
    description: 'Manage family hub features'
  },

  // Monitoring (4)
  {
    id: 'aws_cloudwatch',
    label: 'AWS CloudWatch',
    icon: '☁️',
    category: 'monitoring',
    color: '#EF4444',
    description: 'View AWS CloudWatch metrics and logs'
  },
  {
    id: 'website_status',
    label: 'Website Status',
    icon: '🌐',
    category: 'monitoring',
    color: '#EF4444',
    description: 'Monitor website uptime and status'
  },
  {
    id: 'api_health',
    label: 'API Health',
    icon: '🏥',
    category: 'monitoring',
    color: '#EF4444',
    description: 'Monitor API health checks and incidents'
  },
  {
    id: 'ecs_monitor',
    label: 'ECS Monitor',
    icon: '🐳',
    category: 'monitoring',
    color: '#EF4444',
    description: 'Monitor ECS container services'
  },
  {
    id: 'customer_analytics',
    label: 'Customer Analytics',
    icon: '📊',
    category: 'monitoring',
    color: '#EF4444',
    description: 'View customer behavior analytics'
  },

  // System (3)
  {
    id: 'audit_log',
    label: 'Audit Log',
    icon: '📜',
    category: 'system',
    color: '#64748B',
    description: 'View system audit trail'
  },
  {
    id: 'system_settings',
    label: 'System Settings',
    icon: '⚙️',
    category: 'system',
    color: '#64748B',
    description: 'Configure system-wide settings'
  },
  {
    id: 'product_intelligence',
    label: 'Product Intelligence',
    icon: '🔍',
    category: 'system',
    color: '#1A5F7A',
    description: 'Track bugs, feature improvements, and marketing items'
  },
  {
    // Grokbot: the bot org, recommendations, Grant's decisions and instructions.
    // Deliberately in no default permission set: only super admins get it.
    id: 'bot_operations',
    label: 'Grokbot (Bot Operations)',
    icon: '🤖',
    category: 'system',
    color: '#7C3AED',
    description: 'Bot org, recommendations, Grant decisions and instructions'
  },
];

// Helper functions
export const getResourceById = (id: string): PermissionResource | undefined => {
  return PERMISSION_RESOURCES.find(r => r.id === id);
};

export const getResourcesByCategory = (categoryId: string): PermissionResource[] => {
  return PERMISSION_RESOURCES.filter(r => r.category === categoryId);
};

export const getCategoryById = (id: string): PermissionCategory | undefined => {
  return PERMISSION_CATEGORIES.find(c => c.id === id);
};

export const getCategoryColor = (categoryId: string): string => {
  const category = getCategoryById(categoryId);
  return category?.color || '#64748B';
};

// Permission checking utilities

/**
 * Check if email is a super admin (hardcoded for safety)
 */
export const isSuperAdminEmail = (email: string | undefined): boolean => {
  if (!email) return false;
  return SUPER_ADMIN_EMAILS.includes(email.toLowerCase());
};

/**
 * Check if user has access (view_only or full) to a resource
 * Super admin emails always have access
 */
export const hasAccess = (permissions: Permission[], resourceId: string, userEmail?: string): boolean => {
  // Super admins always have access
  if (isSuperAdminEmail(userEmail)) {
    return true;
  }
  const permission = permissions.find(p => p.resource === resourceId);
  return permission?.access_level === 'full' || permission?.access_level === 'view_only';
};

/**
 * Check if user has full access to a resource
 * Super admin emails always have full access
 */
export const hasFullAccess = (permissions: Permission[], resourceId: string, userEmail?: string): boolean => {
  // Super admins always have full access
  if (isSuperAdminEmail(userEmail)) {
    return true;
  }
  const permission = permissions.find(p => p.resource === resourceId);
  return permission?.access_level === 'full';
};

/**
 * Get access level for a resource
 * Super admin emails always get 'full'
 */
export const getAccessLevel = (permissions: Permission[], resourceId: string, userEmail?: string): AccessLevel => {
  // Super admins always have full access
  if (isSuperAdminEmail(userEmail)) {
    return 'full';
  }
  const permission = permissions.find(p => p.resource === resourceId);
  return permission?.access_level || 'none';
};

// Get all resource IDs for validation
export const getAllResourceIds = (): string[] => {
  return PERMISSION_RESOURCES.map(r => r.id);
};

// Validate permission format
export const isValidPermission = (permission: Permission): boolean => {
  const validResources = getAllResourceIds();
  const validLevels: AccessLevel[] = ['full', 'view_only', 'none'];
  return validResources.includes(permission.resource) && validLevels.includes(permission.access_level);
};

// Count helpers for stats
export const countFullAccess = (permissions: Permission[]): number => {
  return permissions.filter(p => p.access_level === 'full').length;
};

export const countViewOnly = (permissions: Permission[]): number => {
  return permissions.filter(p => p.access_level === 'view_only').length;
};

export const countTotalPermissions = (permissions: Permission[]): number => {
  return permissions.filter(p => p.access_level !== 'none').length;
};

// Default permission sets for quick assignment
export const DEFAULT_PERMISSION_SETS = {
  // Super admin gets everything with full access
  super_admin: PERMISSION_RESOURCES.map(r => ({
    resource: r.id,
    access_level: 'full' as AccessLevel
  })),

  // Finance gets financial resources only
  finance: [
    { resource: 'overview', access_level: 'full' as AccessLevel },
    { resource: 'revenue_analytics', access_level: 'full' as AccessLevel },
    { resource: 'cost_management', access_level: 'full' as AccessLevel },
    { resource: 'stripe_discounts', access_level: 'full' as AccessLevel },
    { resource: 'subscription_analytics', access_level: 'view_only' as AccessLevel },
    { resource: 'user_management', access_level: 'view_only' as AccessLevel },
    // Wedding books carry revenue, so finance can read the portfolio. `full`
    // (suspend / reactivate / extend deadline / resend invitation) stays
    // super-admin-only.
    { resource: 'wedding_books', access_level: 'view_only' as AccessLevel },
  ],

  // Marketing gets non-financial resources
  marketing: [
    { resource: 'overview', access_level: 'view_only' as AccessLevel },
    { resource: 'user_management', access_level: 'full' as AccessLevel },
    { resource: 'chapter_creation', access_level: 'full' as AccessLevel },
    { resource: 'quest_creation', access_level: 'full' as AccessLevel },
    { resource: 'legacy_quests', access_level: 'full' as AccessLevel },
    { resource: 'daily_inspirations', access_level: 'full' as AccessLevel },
    { resource: 'pdf_templates', access_level: 'full' as AccessLevel },
    { resource: 'ai_styles', access_level: 'full' as AccessLevel },
    { resource: 'ai_moods', access_level: 'full' as AccessLevel },
    { resource: 'customer_support', access_level: 'full' as AccessLevel },
    { resource: 'in_app_text_email', access_level: 'full' as AccessLevel },
    { resource: 'email_templates', access_level: 'full' as AccessLevel },
    { resource: 'email_triggers', access_level: 'full' as AccessLevel },
    { resource: 'family_collaboration', access_level: 'full' as AccessLevel },
    { resource: 'customer_analytics', access_level: 'view_only' as AccessLevel },
    { resource: 'product_intelligence', access_level: 'full' as AccessLevel },
    // Marketing reads the cross-sell funnel; the operator actions stay
    // super-admin-only.
    { resource: 'wedding_books', access_level: 'view_only' as AccessLevel },
  ],

  // View only gets limited read access
  view_only: [
    { resource: 'overview', access_level: 'view_only' as AccessLevel },
    { resource: 'user_management', access_level: 'view_only' as AccessLevel },
    { resource: 'customer_analytics', access_level: 'view_only' as AccessLevel },
  ],
};

// Map legacy roles to permission sets
export const mapLegacyRoleToPermissions = (role: string): Permission[] => {
  switch (role) {
    case 'super_admin':
      return DEFAULT_PERMISSION_SETS.super_admin;
    case 'finance':
    case 'finance_admin':
      return DEFAULT_PERMISSION_SETS.finance;
    case 'marketing':
    case 'product_admin':
      return DEFAULT_PERMISSION_SETS.marketing;
    case 'view_only':
    default:
      return DEFAULT_PERMISSION_SETS.view_only;
  }
};
